import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { report, recordPerf } from "@/lib/diagnostics/report";
import { classifySupabaseError } from "@/lib/diagnostics/classify";

export const Route = createFileRoute("/admin/results")({ component: ResultsAdmin });

function ResultsAdmin() {
  const qc = useQueryClient();
  const { data: games } = useQuery({
    queryKey: ["r_games"],
    queryFn: async () => (await supabase.from("games").select("id,name")).data ?? [],
  });
  const { data: results } = useQuery({
    queryKey: ["r_results"],
    queryFn: async () =>
      (await supabase.from("results").select("*, games(name)").order("result_date", { ascending: false }).limit(100)).data ?? [],
  });
  const { data: logs } = useQuery({
    queryKey: ["r_edit_logs"],
    queryFn: async () =>
      (await supabase.from("result_edit_logs").select("*").order("created_at", { ascending: false }).limit(50)).data ?? [],
  });

  const today = new Date().toISOString().slice(0, 10);
  const [gameId, setGameId] = useState("");
  const [value, setValue] = useState("");
  const [date, setDate] = useState(today);
  const [saving, setSaving] = useState(false);

  // Custom bet window (Asia/Kolkata wall-clock, may span multiple dates)
  const [useCustom, setUseCustom] = useState(false);
  const [startAt, setStartAt] = useState(""); // datetime-local (IST) — bet window start
  const [endAt, setEndAt] = useState("");     // datetime-local (IST) — bet window end
  // Optional late-bet refund cutoff (bets between cutoff and end are refunded)
  const [useRefundCutoff, setUseRefundCutoff] = useState(false);
  const [cutoffAt, setCutoffAt] = useState(""); // datetime-local (IST)
  const [preview, setPreview] = useState<any | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Edit dialog state
  const [editing, setEditing] = useState<any | null>(null);
  const [eGame, setEGame] = useState("");
  const [eDate, setEDate] = useState("");
  const [eValue, setEValue] = useState("");
  const [eReason, setEReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [correctionPreview, setCorrectionPreview] = useState<any | null>(null);
  const [allowDebt, setAllowDebt] = useState(false);

  // Convert an IST datetime-local ("YYYY-MM-DDTHH:MM") to a UTC ISO string.
  function istLocalToUtcIso(local: string): string {
    // Treat the input as Asia/Kolkata wall-clock time.
    return new Date(`${local}:00+05:30`).toISOString();
  }
  const fmtIstLocal = (local: string) => {
    if (!local) return "—";
    return new Date(`${local}:00+05:30`).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  async function openPreview() {
    if (!gameId) return toast.error("Pick a game");
    if (!value) return toast.error("Enter winning number");
    if (!/^\d{1,2}$/.test(value) || Number(value) < 0 || Number(value) > 99) {
      return toast.error("Winning number must be 00–99");
    }
    if (useCustom) {
      if (!startAt || !endAt) return toast.error("Enter bet window start and end");
      if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
        return toast.error("Bet window end must be after start");
      }
      if (useRefundCutoff) {
        if (!cutoffAt) return toast.error("Enter refund cutoff date & time");
        const s = new Date(startAt).getTime();
        const c = new Date(cutoffAt).getTime();
        const e = new Date(endAt).getTime();
        if (c < s) return toast.error("Refund cutoff must be on or after bet window start");
        if (c > e) return toast.error("Refund cutoff must be on or before bet window end");
      }
    }
    // Settlement cutoff sent to server:
    //  - custom + refund cutoff on  → cutoffAt
    //  - custom + refund cutoff off → endAt (no late-bet refund window)
    const settlementCutoffIso = useCustom
      ? istLocalToUtcIso(useRefundCutoff ? cutoffAt : endAt)
      : null;
    const endIso = useCustom && useRefundCutoff ? istLocalToUtcIso(endAt) : null;

    setLoadingPreview(true);
    const { data, error } = await supabase.rpc("admin_publish_result_preview", {
      _game_id: gameId,
      _result_date: date,
      _mode: useCustom ? "custom" : "normal",
      _eligible_start_at: useCustom ? istLocalToUtcIso(startAt) : null,
      _eligible_cutoff_at: settlementCutoffIso,
      _result_value: value,
      _eligible_end_at: endIso,
      _refund_after_cutoff: useCustom && useRefundCutoff,
    } as any);
    setLoadingPreview(false);
    if (error) return toast.error(error.message);
    setPreview(data);
    setPreviewOpen(true);
  }

  async function confirmPublish() {
    if (preview?.overlap) {
      return toast.error("This window overlaps an existing cycle. Adjust the times or display date.");
    }
    setSaving(true);
    const settlementCutoffIso = useCustom
      ? istLocalToUtcIso(useRefundCutoff ? cutoffAt : endAt)
      : null;
    const endIso = useCustom && useRefundCutoff ? istLocalToUtcIso(endAt) : null;
    const t0 = Date.now();
    const { error } = await supabase.rpc("admin_publish_result" as any, {
      _game_id: gameId,
      _result_date: date,
      _result_value: value,
      _mode: useCustom ? "custom" : "normal",
      _eligible_start_at: useCustom ? istLocalToUtcIso(startAt) : null,
      _eligible_cutoff_at: settlementCutoffIso,
      _eligible_end_at: endIso,
      _refund_after_cutoff: useCustom && useRefundCutoff,
    } as any);
    setSaving(false);
    setPreviewOpen(false);
    recordPerf("result_settlement", Date.now() - t0, {
      metadata: { game_id: gameId, mode: useCustom ? "custom" : "normal" },
    });
    if (error) {
      const { category, code } = classifySupabaseError(error);
      const { error_id, safe_message } = report({
        category: category === "database" ? "result" : category,
        code,
        stage: "admin_publish_result",
        duration_ms: Date.now() - t0,
        technical_message: error.message,
        severity: "critical",
        wallet_impact: "unknown",
        metadata: { game_id: gameId, result_date: date },
      });
      return toast.error(`${safe_message} Error ID: ${error_id}`);
    }
    toast.success("Result published & bets processed");
    setValue("");
    setUseCustom(false);
    setStartAt("");
    setEndAt("");
    setUseRefundCutoff(false);
    setCutoffAt("");
    qc.invalidateQueries();
  }



  async function del(id: string) {
    toast.error("Published results cannot be deleted. Use Edit with a reason instead.");
  }

  function openEdit(r: any) {
    setEditing(r);
    setEGame(r.game_id);
    setEDate(r.result_date);
    setEValue(String(r.result_value));
    setEReason("");
  }

  async function submitEdit() {
    if (!editing) return;
    if (eReason.trim().length < 5) return toast.error("Reason must be at least 5 characters");
    if (!eGame || !eValue || !eDate) return toast.error("All fields are required");

    const isRealCorrection =
      String(editing.result_value) !== String(eValue) ||
      editing.game_id !== eGame ||
      editing.result_date !== eDate;

    // Real correction → require preview + confirmation token
    if (isRealCorrection && !correctionPreview) {
      setEditSaving(true);
      const { data, error } = await supabase.rpc("preview_result_correction" as any, {
        _result_id: editing.id,
        _new_result_value: eValue,
      });
      setEditSaving(false);
      if (error) return toast.error(error.message);
      setCorrectionPreview(data);
      setAllowDebt(false);
      setConfirmOpen(true);
      return;
    }

    setEditSaving(true);
    const { error } = await supabase.rpc("admin_edit_result", {
      _result_id: editing.id,
      _new_game_id: eGame,
      _new_result_date: eDate,
      _new_result_value: eValue,
      _reason: eReason.trim(),
      _preview_id: correctionPreview?.preview_id ?? null,
      _confirmation_hash: correctionPreview?.confirmation_hash ?? null,
      _allow_debt: allowDebt,
    } as any);
    setEditSaving(false);
    setConfirmOpen(false);
    if (error) return toast.error(error.message);
    toast.success("Result updated and bets re-settled");
    setEditing(null);
    setCorrectionPreview(null);
    setAllowDebt(false);
    qc.invalidateQueries();
  }

  const gameName = (id: string) => (games ?? []).find((g: any) => g.id === id)?.name ?? "—";
  const fmtIst = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Results</h1>

      <div className="rounded-2xl bg-card p-4 shadow-card space-y-3">
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <Label>Game *</Label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={gameId}
              onChange={(e) => setGameId(e.target.value)}
            >
              <option value="">Select…</option>
              {(games ?? []).map((g: any) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Result date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Winning number * (00–99)</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 35" />
          </div>
          <div className="flex items-end">
            <Button onClick={openPreview} disabled={saving || loadingPreview} className="w-full gradient-primary">
              {loadingPreview ? "Checking…" : "Publish result"}
            </Button>
          </div>
        </div>

        {/* Custom bet window (optional, may cross dates) */}
        <div className="rounded-lg border p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={useCustom}
              onChange={(e) => setUseCustom(e.target.checked)}
            />
            Use Custom Bet Date &amp; Time Range (Asia/Kolkata)
          </label>
          {useCustom && (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Bet Window Start (IST)</Label>
                <Input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                />
              </div>
              <div>
                <Label>Bet Window End (IST)</Label>
                <Input
                  type="datetime-local"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                />
              </div>
              <p className="md:col-span-2 text-xs text-muted-foreground">
                Only bets placed inside this window (start ≤ time &lt; end) will participate
                in the result. The window may span across dates. The "Result date" above is
                used only for display and history.
              </p>

              {/* Optional late-bet refund cutoff */}
              <div className="md:col-span-2 rounded-md border border-dashed p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={useRefundCutoff}
                    onChange={(e) => setUseRefundCutoff(e.target.checked)}
                  />
                  Refund bets placed after cutoff
                </label>
                {useRefundCutoff && (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>Cutoff Date &amp; Time (IST)</Label>
                      <Input
                        type="datetime-local"
                        value={cutoffAt}
                        onChange={(e) => setCutoffAt(e.target.value)}
                      />
                    </div>
                    <p className="md:col-span-2 text-xs text-muted-foreground">
                      Bets placed after this cutoff and before Bet Window End will be
                      refunded automatically and will not participate in the result.
                      Cutoff must be within the bet window (start ≤ cutoff ≤ end).
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Publish confirmation dialog */}
      <Dialog open={previewOpen} onOpenChange={(o) => !o && setPreviewOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Publish</DialogTitle>
            <DialogDescription>Review before publishing this result.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            <div><b>Game:</b> {gameName(gameId)}</div>
            <div><b>Result display date:</b> {date}</div>
            <div><b>Winning number:</b> {value}</div>
            <div><b>Settlement mode:</b> {useCustom ? "Custom Window" : "Normal (whole day)"}</div>
            {useCustom && (
              <>
                <div><b>Bet Window Start (IST):</b> {fmtIstLocal(startAt)}</div>
                {useRefundCutoff && (
                  <div><b>Refund Cutoff (IST):</b> {fmtIstLocal(cutoffAt)}</div>
                )}
                <div><b>Bet Window End (IST):</b> {fmtIstLocal(endAt)}</div>
              </>
            )}
            <div className="pt-2 border-t mt-2 space-y-0.5">
              {useCustom && (
                <div><b>Total bets in bet window:</b> {preview?.total_count ?? 0} (₹{Number(preview?.total_amount ?? 0).toLocaleString("en-IN")})</div>
              )}
              <div><b>Result-eligible bets:</b> {preview?.settle_count ?? 0} (₹{Number(preview?.settle_amount ?? 0).toLocaleString("en-IN")})</div>
              {useCustom && useRefundCutoff && (
                <div className="text-amber-700 dark:text-amber-400">
                  <b>Late-bet refunds:</b> {preview?.refund_count ?? 0} bets — ₹{Number(preview?.refund_amount ?? 0).toLocaleString("en-IN")} total refund
                </div>
              )}
              <div><b>Estimated winners:</b> {preview?.winner_count ?? 0} (₹{Number(preview?.winner_amount ?? 0).toLocaleString("en-IN")} staked)</div>
              <div><b>Estimated payout:</b> ₹{Number(preview?.payout_total ?? 0).toLocaleString("en-IN")} <span className="text-muted-foreground">(× {preview?.multiplier ?? 100})</span></div>
              <p className="text-xs text-muted-foreground pt-1">Already settled/refunded bets are excluded from these counts.</p>
              {preview?.overlap && (
                <div className="mt-2 rounded-md bg-destructive/10 text-destructive px-2 py-1.5 text-xs font-semibold">
                  ⚠ This window (or display date) overlaps an existing cycle for this game. Adjust before publishing.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              className="gradient-primary"
              onClick={confirmPublish}
              disabled={saving || !!preview?.overlap}
            >
              {saving ? "Publishing…" : "Confirm & Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>




      <div className="overflow-x-auto rounded-2xl bg-card shadow-card">
        <table className="w-full min-w-[640px] text-sm whitespace-nowrap">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Game</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Result</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(results ?? []).map((r: any) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3 font-semibold">{r.games?.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.result_date}</td>
                <td className="px-4 py-3">
                  <span className="rounded bg-primary px-3 py-1 font-black text-primary-foreground">{r.result_value}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <button onClick={() => del(r.id)} className="text-destructive text-xs">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit history / audit log */}
      <div className="rounded-2xl bg-card p-4 shadow-card">
        <h2 className="mb-3 text-lg font-bold">Result Edit History</h2>
        {(logs ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No edits yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Game (old → new)</th>
                  <th className="py-2 pr-3">Date (old → new)</th>
                  <th className="py-2 pr-3">Result (old → new)</th>
                  <th className="py-2 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(logs ?? []).map((l: any) => (
                  <tr key={l.id} className="border-t">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtIst(l.created_at)}</td>
                    <td className="py-2 pr-3">{gameName(l.old_game_id)} → {gameName(l.new_game_id)}</td>
                    <td className="py-2 pr-3">{l.old_result_date} → {l.new_result_date}</td>
                    <td className="py-2 pr-3 font-bold">{l.old_result_value} → {l.new_result_value}</td>
                    <td className="py-2 pr-3">{l.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Result</DialogTitle>
            <DialogDescription>Update the game, date or winning number. Bets will be re-settled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Game</Label>
              <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={eGame} onChange={(e) => setEGame(e.target.value)}>
                {(games ?? []).map((g: any) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div><Label>Date</Label><Input type="date" value={eDate} onChange={(e) => setEDate(e.target.value)} /></div>
            <div><Label>Result Number</Label><Input value={eValue} onChange={(e) => setEValue(e.target.value)} /></div>
            <div>
              <Label>Reason (required)</Label>
              <Textarea value={eReason} onChange={(e) => setEReason(e.target.value)} placeholder="Why is this result being edited?" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              className="gradient-primary"
              onClick={submitEdit}
              disabled={editSaving}
            >
              {editSaving ? "Checking impact…" : "Preview & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) { setCorrectionPreview(null); setAllowDebt(false); } }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Result Correction</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-left">
                {correctionPreview?.impact ? (
                  <>
                    <div className="rounded-md bg-muted p-3 space-y-1 text-xs">
                      <div><strong>Old winning number:</strong> {correctionPreview.impact.old_result_value ?? "—"}</div>
                      <div><strong>New winning number:</strong> {correctionPreview.impact.new_result_value}</div>
                      <div><strong>Old winners:</strong> {correctionPreview.impact.old_winner_count} (₹{Number(correctionPreview.impact.total_reversal_required).toLocaleString("en-IN")} to reverse)</div>
                      <div><strong>New winners:</strong> {correctionPreview.impact.new_winner_count} (₹{Number(correctionPreview.impact.total_new_payout).toLocaleString("en-IN")} to pay)</div>
                      {Number(correctionPreview.impact.unrecoverable_amount) > 0 && (
                        <div className="text-destructive font-semibold pt-1 border-t border-border/60">
                          ⚠ Unrecoverable: ₹{Number(correctionPreview.impact.unrecoverable_amount).toLocaleString("en-IN")} — users already spent part of their old winnings.
                        </div>
                      )}
                    </div>
                    {Number(correctionPreview.impact.unrecoverable_amount) > 0 && (
                      <label className="flex items-start gap-2 text-xs">
                        <input type="checkbox" checked={allowDebt} onChange={(e) => setAllowDebt(e.target.checked)} className="mt-0.5" />
                        <span>Create a wallet-debt record for the unrecoverable amount and proceed anyway.</span>
                      </label>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Confirmation token expires at {new Date(correctionPreview.expires_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}.
                    </p>
                  </>
                ) : (
                  <p>Loading impact preview…</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={editSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitEdit} disabled={editSaving || !correctionPreview}>
              {editSaving ? "Applying…" : "Confirm correction"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
